import { SearchIcon } from '@chakra-ui/icons'
import {
  Button,
  Checkbox,
  CloseButton,
  Divider,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  Text,
  useDisclosure
} from '@chakra-ui/react'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import FilterIcon from '@/ui/assets/FilterIcon'

import { useCAADailyRecordsContext } from '../context/CAADailyRecordsContext'

export default function CAADailyRecordsFilterMenu() {
  const { t } = useTranslation()

  const {
    isOpen: isFilterMenuOpen,
    onToggle: onFilterMenuToggle,
    onClose: onFilterMenuClose
  } = useDisclosure()

  const {
    filter,
    setDate,
    setPersonaName,
    setAvailability,
    setTypeCAA,
    rawInputs
  } = useCAADailyRecordsContext()

  const bg =
    filter.date || filter.personaName || filter.availability || filter.typeCAA
      ? 'custom.blue.darker'
      : undefined

  const color =
    filter.date || filter.personaName || filter.availability || filter.typeCAA
      ? '#FFFFFF'
      : undefined

  return (
    <Menu isOpen={isFilterMenuOpen} onClose={onFilterMenuClose}>
      <MenuButton
        as={Button}
        variant="outline"
        onClick={onFilterMenuToggle}
        rightIcon={<FilterIcon color={color} />}
        borderWidth="1px"
        borderRadius="md"
        py="2"
        px="3"
        w="130px"
        {...(bg && { bg })}
        _hover={{ textDecoration: 'none' }}
        _active={{ textDecoration: 'none' }}
      >
        <Text w="fit-content" fontWeight="500" color={color}>
          {t('therapists.documents.request.filter.button')}
        </Text>
      </MenuButton>

      <MenuList
        w={'390px'}
        px="4"
        pb="4"
        display="flex"
        flexDir="column"
        gap="4"
        zIndex={2}
      >
        <Flex
          justifyContent={'space-between'}
          w={'full'}
          alignItems={'center'}
          h={'fit-content'}
        >
          <Text fontWeight={'500'} color={'custom.gray.100'}>
            {t('admin.documents.report.filter.menu.title')}
          </Text>
          <Flex gap={2} alignItems="center">
            <CloseButton onClick={onFilterMenuClose} />
          </Flex>
        </Flex>
        <Divider />
        <Flex direction={'column'} gap={2}>
          <Flex justify={'end'}>
            <Button
              variant={'link'}
              colorScheme={'blue'}
              color={'custom.blue.darker'}
              onClick={() => setPersonaName('')}
            >
              {t('operational.session.filter.clearFilter')}
            </Button>
          </Flex>
          <InputGroup>
            <InputLeftElement>
              <SearchIcon />
            </InputLeftElement>
            <Input
              value={rawInputs.personaName}
              onChange={(e) => setPersonaName(e.target.value)}
              placeholder={t('operational.session.filter.searchPlaceholder')}
            />
          </InputGroup>
        </Flex>
        <Divider />
        <Flex direction={'column'} gap={2}>
          <Text color={'custom.gray.300'} fontSize={'16px'} fontWeight={500}>
            {t('operational.session.filter.dateLabel')}
          </Text>
          <Input
            value={filter.date}
            onChange={(e) => setDate(e.currentTarget.value)}
            type="date"
            max={moment().format(moment.HTML5_FMT.DATE)}
          />
        </Flex>
        <Divider />
        <Flex direction={'column'} gap={2}>
          <Flex justify={'space-between'}>
            <Text color={'custom.gray.300'} fontSize={'16px'} fontWeight={500}>
              {t('caa.filter.byUses.label')}
            </Text>
            <Button
              variant={'link'}
              colorScheme={'blue'}
              color={'custom.blue.darker'}
              onClick={() => setTypeCAA(undefined)}
            >
              {t('operational.session.filter.clearFilter')}
            </Button>
          </Flex>
          <Flex direction={'column'} gap={2}>
            <Checkbox
              isChecked={filter.typeCAA === 'MODELING'}
              onChange={() => {
                if (filter.typeCAA === 'MODELING') {
                  setTypeCAA(undefined)
                } else {
                  setTypeCAA('MODELING')
                }
              }}
              size="lg"
            >
              {t('caa.table.caaTypes.MODELING')}
            </Checkbox>
            <Checkbox
              isChecked={filter.typeCAA === 'CUSTOMER_USE'}
              onChange={() => {
                if (filter.typeCAA === 'CUSTOMER_USE') {
                  setTypeCAA(undefined)
                } else {
                  setTypeCAA('CUSTOMER_USE')
                }
              }}
              size="lg"
            >
              {t('caa.table.caaTypes.CUSTOMER_USE')}
            </Checkbox>
          </Flex>
        </Flex>
        <Divider />
        <Flex direction={'column'} gap={2}>
          <Flex justify={'space-between'}>
            <Text color={'custom.gray.300'} fontSize={'16px'} fontWeight={500}>
              {t('caa.filter.byAvailability.label')}
            </Text>
            <Button
              variant={'link'}
              colorScheme={'blue'}
              color={'custom.blue.darker'}
              onClick={() => setAvailability(undefined)}
            >
              {t('operational.session.filter.clearFilter')}
            </Button>
          </Flex>
          <Flex direction={'column'} gap={2}>
            <Checkbox
              isChecked={filter.availability === 'AVAILABLE'}
              onChange={() => {
                if (filter.availability === 'AVAILABLE') {
                  setAvailability(undefined)
                } else {
                  setAvailability('AVAILABLE')
                }
              }}
              size="lg"
            >
              {t('caa.table.availabilityTypes.AVAILABLE')}
            </Checkbox>
            <Checkbox
              isChecked={filter.availability === 'UNAVAILABLE'}
              onChange={() => {
                if (filter.availability === 'UNAVAILABLE') {
                  setAvailability(undefined)
                } else {
                  setAvailability('UNAVAILABLE')
                }
              }}
              size="lg"
            >
              {t('caa.table.availabilityTypes.UNAVAILABLE')}
            </Checkbox>
          </Flex>
        </Flex>
      </MenuList>
    </Menu>
  )
}
